import { useState, useEffect, useCallback, useMemo } from "react";
import { debounce } from "../../../utils/debounce";
import { ExistingSolicitorResult } from "./types";

// Type guard to validate API response
function isExistingSolicitorResult(obj: unknown): obj is ExistingSolicitorResult {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "firmId" in obj &&
    typeof obj.firmId === "number" &&
    "name" in obj &&
    typeof obj.name === "string"
  );
}

function isSolicitorResultArray(data: unknown): data is ExistingSolicitorResult[] {
  return Array.isArray(data) && data.every(isExistingSolicitorResult);
}

interface UseSolicitorNameSearchResult {
  solicitors: ExistingSolicitorResult[];
  isLoading: boolean;
  error: string | null;
}

export function useSolicitorNameSearch(query: string, enabled: boolean): UseSolicitorNameSearchResult {
  const [solicitors, setSolicitors] = useState<ExistingSolicitorResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSolicitors = useCallback(async (searchQuery: string) => {
    // Only fetch if query is at least 2 characters
    if (searchQuery.length < 2) {
      setSolicitors([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        query: searchQuery,
        limit: "5",
      });

      const response = await fetch(`/solicitors/byname?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`Failed to search solicitors: ${response.statusText}`);
      }

      const data: unknown = await response.json();

      if (!isSolicitorResultArray(data)) {
        throw new Error("Invalid response format from solicitors search API");
      }

      setSolicitors(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred while searching solicitors");
      setSolicitors([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Type-safe wrapper for debounced fetch
  const debouncedFetch = useMemo(
    () => debounce((...args: unknown[]) => fetchSolicitors(args[0] as string), 500),
    [fetchSolicitors],
  );

  useEffect(() => {
    if (!enabled) {
      setSolicitors([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    if (query.trim().length === 0) {
      setSolicitors([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    debouncedFetch(query.trim());
  }, [query, enabled, debouncedFetch]);

  return { solicitors, isLoading, error };
}
